import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Modal, Form, Input, Button, Upload, Typography, Flex, Table, Tag, Card, Spin } from "antd"
import { CheckOutlined, DeleteOutlined, InfoCircleOutlined } from "@ant-design/icons"
import styles from "./DocumentAdded.module.css"
import LabeledField from "pagesV2/client/Profile/Tabs/ProfileInfo/LabeledField"
import ExperienceSelector from "./ExperienceSelector"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { UploadIcon } from "ui/Icon/DSIcon/UploadIcon"
import DiscardConfirm from "ui/DiscardModal"
import { File as FileIcon } from "ui/Icon/DSIcon/File"
import { getTripList } from "store/trips"
import { createDigitalWaiver, updateDigitalWaiver } from "store/digital-waiver"
import { toaster } from "components/common/Toast"

const { Text } = Typography
const { Title } = Typography
export const DocumentsAddedModal = ({ setIsLoading, isLoading, visible, onCancel, onSave, initialData, isView }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [selectedExperiences, setSelectedExperiences] = useState([]) // experience ids
  const [fileList, setFileList] = useState([])
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [tripsState, setTripsState] = useState([])
  const [initialValues, setInitialValues] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const { loading } = useSelector((state) => state?.digitalWaiver)
  // Set initial values when modal opens or initialData changes
  useEffect(() => {
    if (visible) {
      if (initialData) {
        // Extract trip IDs from assigned_trips objects
        const tripIds = initialData.assigned_trips?.map((trip) => String(trip.id)) || []
        // Set form fields for editing
        form.setFieldsValue({
          documentName: initialData.name,
          experiences: tripIds
        })

        // Set selected experiences
        setSelectedExperiences(tripIds)
        // Set file list with existing document
        if (initialData.document_url) {
          const fileName = initialData.document_filename || initialData.document_url.split("/").pop() || "document.pdf"
          const now = new Date()

          // Initial file object with placeholder data
          const initialFileObj = {
            uid: `existing-${Date.now()}`,
            lastModified: now.getTime(),
            lastModifiedDate: now.toISOString(),
            name: fileName,
            type: "application/pdf",
            percent: 0,
            status: "uploading",
            url: initialData.document_url
          }

          setFileList([initialFileObj])
          // Fetch the actual document
          const fetchDocument = async () => {
            try {
              const response = await fetch(initialData.document_url)
              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
              }

              // Get the blob and create a file object
              const blob = await response.blob()
              const file = new File([blob], fileName, {
                type: blob.type || "application/pdf",
                lastModified: now.getTime()
              })

              // Create the final file object with all properties
              const fileObj = {
                ...initialFileObj,
                size: blob.size,
                status: "done",
                originFileObj: file
              }

              // Update file list with actual file data
              setFileList([fileObj])

              // Set form field with the actual file
              form.setFieldsValue({ documentFile: file })

              // Create and set object URL for preview
              const objectUrl = URL.createObjectURL(blob)
              setPdfUrl(objectUrl)
              setIsLoading(false)
              // Clean up function to revoke object URL on unmount
              return () => {
                URL.revokeObjectURL(objectUrl)
              }
            } catch (error) {
              console.error("Error fetching document:", error)
              // Update file list with error state
              setFileList([
                {
                  ...initialFileObj,
                  status: "error",
                  error: "Failed to load document"
                }
              ])
            }
          }

          fetchDocument()
        }
      } else {
        // Reset form for new document
        form.resetFields()
        setFileList([])
        setSelectedExperiences([])
        setPdfUrl(null)
      }

      // Set initial values for change detection
      setInitialValues({
        documentName: initialData?.name || form.getFieldValue("documentName") || "",
        experiences: initialData?.assigned_trips?.map((trip) => String(trip.id)) || form.getFieldValue("experiences") || [],
        fileList: initialData?.document_url
          ? [
              {
                uid: "-1",
                name: initialData.name || "document.pdf",
                status: "done",
                url: initialData.document_url
              }
            ]
          : [...fileList]
      })
    }
  }, [visible, initialData])
  console.log(pdfUrl, "pdfurlldfd")
  // Check if form has unsaved changes
  const hasUnsavedChanges = () => {
    if (!initialValues) return false

    const currentValues = {
      documentName: form.getFieldValue("documentName") || "",
      experiences: form.getFieldValue("experiences") || [],
      fileList: [...fileList]
    }

    return (
      initialValues.documentName !== currentValues.documentName ||
      JSON.stringify(initialValues.experiences) !== JSON.stringify(currentValues.experiences) ||
      initialValues.fileList.length !== currentValues.fileList.length
    )
  }

  const handleCancel = () => {
    if (hasUnsavedChanges()) {
      setShowDiscardConfirm(true)
    } else {
      resetForm()
      onCancel()
    }
  }

  const resetForm = () => {
    form.resetFields()
    setFileList([])
    setPdfUrl(null)
    setSelectedExperiences([])
  }

  const handleConfirmDiscard = () => {
    // Clear file list and PDF preview
    setFileList([])
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl)
      setPdfUrl(null)
    }

    // Reset form fields and close modals
    form.resetFields()
    setShowDiscardConfirm(false)
    onCancel()
  }

  const handleCancelDiscard = () => {
    setShowDiscardConfirm(false)
  }

  const handleUpload = ({ fileList: newFileList }) => {
    setIsUploading(true)
    setFileList(newFileList)
    if (newFileList.length > 0) {
      const file = newFileList[0].originFileObj
      if (file) {
        const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")
        if (!isPdf) {
          form.setFields([{ name: "documentFile", value: file, errors: ["Only PDF files are acceptable."] }])
        } else {
          // Create a simple object URL for the file
          const url = URL.createObjectURL(file)
          setPdfUrl(url)
          form.setFields([{ name: "documentFile", value: file, errors: [] }])
        }
      } else {
        form.setFields([{ name: "documentFile", value: newFileList[0]?.originFileObj, errors: [] }])
      }
    } else {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl)
        setPdfUrl(null)
      }
      form.setFields([{ name: "documentFile", value: undefined }])
    }
  }

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl)
      }
    }
  }, [pdfUrl])

  const experiencesMemo = React.useMemo(
    () =>
      tripsState?.map((trip) => ({
        id: String(trip?.id),
        name: trip?.title || "Untitled",
        guests: trip?.guests || 0,
        price: trip?.price ? Number(trip?.price) : 0,
        status: trip?.status,
        currency: "$",
        duration: trip?.duration || "-",
        waiver_count: trip?.waiver_count ?? 0
      })),
    [tripsState]
  )
  // Derive selected experience data instead of storing separate state (prevents extra render)
  const selectedExperienceData = React.useMemo(
    () => (selectedExperiences.length ? experiencesMemo.filter((exp) => selectedExperiences.includes(String(exp.id))) : []),
    [selectedExperiences, experiencesMemo]
  )
  // Guide category to boats state
  useEffect(() => {
    // Fetch trips on mount
    const fetchTrips = async () => {
      try {
        const response = await dispatch(getTripList())
        // The response should be an array of trips
        // if (Array.isArray(response)) {
        //   setTripsState(response)
        // }
         if (response && Array.isArray(response.data)) {
          setTripsState(response.data)
        }
      } catch (error) {
        console.error("Error fetching trips:", error)
        setTripsState([])
      }
    }

    fetchTrips()
  }, [dispatch])

  const onFinish = async (values) => {
    if (!fileList.length && !initialData?.document_url) {
      form.setFields([
        {
          name: "documentFile",
          errors: ["Please upload a document"]
        }
      ])
      return
    }

    // Convert selected experience IDs to numbers for the API
    const tripIds = selectedExperiences.map(Number)

    try {
      const formData = new FormData()
      formData.append("digital_waiver[name]", values.documentName)

      // Only append file if it's a new upload or we're editing without an existing file
      if (fileList.length > 0 && fileList[0].originFileObj && isUploading) {
        formData.append("digital_waiver[document]", fileList[0].originFileObj)
      }
      if (initialData?.document_id && !isUploading) {
        formData.append("digital_waiver[document_id]", initialData.document_id)
      }

      // Add trip IDs to form data
      tripIds.forEach((id) => {
        formData.append("digital_waiver[trip_ids][]", id)
      })

      let result
      if (initialData?.id) {
        // Update existing document
        // formData.append("digital_waiver[id]", initialData.id)
        result = await dispatch(updateDigitalWaiver(initialData.id, formData))
      } else {
        // Create new document
        result = await dispatch(createDigitalWaiver(formData))
      }

      // Show success message
      setIsUploading(false)
      toaster.success(result.message || (initialData?.id ? "Document updated successfully" : "Document created successfully"))

      // Call onSave with the result
      if (onSave) {
        onSave(result)
      }

      // Reset form and close modal on success
      resetForm()
      onCancel()
    } catch (error) {
      const errors = error?.data?.errors
      const mainMessage = error?.data?.message
      if (mainMessage) toaster.error(mainMessage)
      if (errors?.name?.length) {
        form.setFields([{ name: "documentName", errors: [errors.name[0]] }])
      }
      if (errors?.document?.length) {
        form.setFields([{ name: "documentFile", errors: [errors.document[0]] }])
      }
      if (errors?.trip_ids?.length) {
        setErrorMessage(errors.trip_ids[0])
      }
    }
  }
  const columns = [
    {
      title: "",
      align: "center",
      width: 50,
      render: (_value, _record, index) => <Text>{index + 1}</Text>
    },
    {
      title: "Experience",
      dataIndex: "name",
      key: "name",
      width: 200,
      render: (name, record) => (
        <Text className={`${styles.waiverLink} ellipsis block`} fw="500" title={name}>
          {name}
        </Text>
      )
    },
    {
      title: "Guests",
      dataIndex: "guests",
      key: "guests",
      align: "left",
      width: 100,
      render: (guests) => (
        <Text>
          {guests} {guests > 1 ? "guests" : "guest"}
        </Text>
      )
    },
    {
      title: "Price",
      key: "price",
      align: "right",
      width: 100,
      render: (_, record) => (
        <Text fw="400">
          {record.currency}
          {record.price.toFixed(2)}
        </Text>
      )
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "left",
      width: 110,
      render: (status) => {
        const isPublished = String(status).toLowerCase() === "published"
        const displayText = String(status).charAt(0).toUpperCase() + String(status).slice(1)
        return (
          <Tag className={styles.statusTag} color={isPublished ? "green" : "default"} icon={isPublished ? <CheckOutlined /> : null}>
            {displayText}
          </Tag>
        )
      }
    },
    {
      title: "",
      key: "action",
      align: "right",
      width: 50,
      render: (_, record) => (
        <Button
          disabled={isView}
          type="text"
          icon={<DeleteOutlined className={styles.removeIcon} />}
          onClick={() => handleRemoveExperience(record.id)}
          className={styles.removeExperienceButton}
          aria-label="Remove experience"
        />
      )
    }
  ]
  const handleRemoveExperience = (expId) => {
    setSelectedExperiences((prev) => {
      const next = prev.filter((id) => id !== expId)
      // Keep form field in sync so controlled value from Form doesn't push stale tag back
      form.setFieldsValue({ experiences: next })
      return next
    })
  }

  const getModalTitle = () => {
    if (initialData?.id && !isView) {
      return "Edit Digital Waivers & Contracts"
    } else if (isView) {
      return "View Digital Waivers & Contracts"
    } else {
      return t("digital_waiver_and_contracts.add_digital_waiver_and_contracts")
    }
  }

  return (
    <>
      <Modal
        title={getModalTitle()}
        open={visible}
        onCancel={handleCancel}
        footer={null}
        width={800}
        maskClosable={false}
        className={styles.documentsModal}
      >
        <Spin spinning={isLoading}>
          <Form form={form} layout="vertical" onFinish={onFinish} className={styles.form}>
            <Card className={`${styles.card} p-3xl pb-3 mb-m`}>
              <LabeledField
                labelKey={t("digital_waiver_and_contracts.document_name")}
                name="documentName"
                rules={[{ required: true, message: t("digital_waiver_and_contracts.required_document_name") }]}
                className={styles.formItem}
              >
                <Input disabled={isView} placeholder={t("digital_waiver_and_contracts.document_name_placeholder")} />
              </LabeledField>
              <div className={styles.uploadSection}>
                <Form.Item
                  name="documentFile"
                  rules={[{ required: true, message: t("digital_waiver_and_contracts.required_document_upload") }]}
                  valuePropName="fileList"
                  getValueFromEvent={(e) => e && e.fileList}
                  className={styles.formItem}
                >
                  <Text className={styles.uploadLabel}>{t("digital_waiver_and_contracts.upload_document")}</Text>
                  {fileList.length > 0 ? (
                    <Flex align="center" justify="center" gap={16} className={`${styles.uploadedFileContainer}`}>
                      <Flex align="center" gap={2} className={isView ? styles.uploadedFileInfoDisabled : null}>
                        <FileIcon className={styles.fileIcon} />
                        <Text className={styles.uploadedFileName}>{fileList[0].name}</Text>
                      </Flex>
                      <Button
                        type="text"
                        danger
                        onClick={() => handleUpload({ fileList: [] })}
                        className={styles.removeButton}
                        disabled={isView}
                      >
                        Remove
                      </Button>
                    </Flex>
                  ) : (
                    <Upload.Dragger
                      disabled={isView}
                      fileList={fileList}
                      onChange={handleUpload}
                      beforeUpload={(file) => {
                        const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")
                        if (!isPdf) {
                          form.setFields([{ name: "documentFile", errors: ["Only PDF files are acceptable."] }])
                        } else {
                          form.setFields([{ name: "documentFile", errors: [] }])
                        }
                        return false
                      }}
                      multiple={false}
                      accept=".pdf"
                      className={styles.uploadArea}
                    >
                      <div className={styles.uploadContent}>
                        <div className={styles.circle}>
                          <UploadIcon />
                        </div>
                        <div>
                          <Button type="link" className={styles.uploadButton}>
                            {t("digital_waiver_and_contracts.upload")}
                          </Button>
                        </div>
                        <Flex align="center" gap={4}>
                          <InfoCircleOutlined style={{ color: "#8c8c8c", fontSize: 13 }} />
                          <Text style={{ color: "#8c8c8c", fontSize: 13 }}>Only PDF files are acceptable.</Text>
                        </Flex>
                      </div>
                    </Upload.Dragger>
                  )}

                  {/* PDF Preview Section */}
                  {pdfUrl && (
                    <div className={styles.pdfPreviewContainer}>
                      <div className={styles.pdfViewer}>
                        <div className={`mb-xs ${styles.documentTitle}`}>Document Preview</div>
                        <object data={`${pdfUrl}#navpanes=0`} type="application/pdf" className={styles.pdfObject}>
                          <div className={styles.pdfFallback}>
                            Unable to display PDF preview. Please download the file to view it.
                          </div>
                        </object>
                      </div>
                    </div>
                  )}
                </Form.Item>
              </div>
            </Card>

            <Card className={`${styles.assignExperiencesSection} p-3xl`}>
              <Title level={5} className={`${styles.sectionTitle} mb-xl`}>
                {t("digital_waiver_and_contracts.assign_experiences")} <span className="ml-xxs">({selectedExperienceData.length})</span>
              </Title>
              <LabeledField
                name="experiences"
                labelKey={t("digital_waiver_and_contracts.experiences")}
                className="mb-0"
                rules={[{ required: true, message: t("digital_waiver_and_contracts.experience_required") }]}
              >
                <ExperienceSelector
                  experiences={experiencesMemo}
                  value={selectedExperiences}
                  isView={isView}
                  onOpen={async () => {
                    const response = await dispatch(getTripList())
                    // if (Array.isArray(response)) {
                    //   setTripsState(response)
                    // }
                    if (response && Array.isArray(response.data)) {
                      setTripsState(response.data)
                    }
                  }}
                  onChange={(vals) => {
                    setErrorMessage("")
                    const strVals = vals.map(String)
                    // Avoid redundant updates: only set if actually changed
                    if (strVals.length !== selectedExperiences.length || strVals.some((v, i) => v !== selectedExperiences[i])) {
                      setSelectedExperiences(strVals)
                      form.setFieldsValue({ experiences: strVals })
                    }
                  }}
                />
                {selectedExperienceData.length > 0 && (
                  <div>
                    <Title level={5} className={`mt-5 ${styles.selectedExperiencesTitle}`}>
                      {t("digital_waiver_and_contracts.assigned_to_experiences")}
                    </Title>
                    <Table
                      disabled={isView}
                      className={styles.table}
                      dataSource={selectedExperienceData}
                      rowKey="id"
                      pagination={false}
                      columns={columns}
                      scroll={{ y: 300 }}
                    />
                  </div>
                )}
              </LabeledField>
              {errorMessage && (
                <Text type="danger" className={styles.errorMessage}>
                  {errorMessage}
                </Text>
              )}
            </Card>

            <Flex align="center" gap={12} className={`${styles.publishButtonsContainer}`}>
              <Button onClick={onCancel} color="primary" size="large" variant="outlined">
                {t("common.cancel") || "Cancel"}
              </Button>
              {!isView && (
                <Button loading={loading} disabled={loading} color="primary" size="large" htmlType="submit" variant="solid">
                  {t("digital_waiver_and_contracts.save_and_publish") || "Save & Publish"}
                </Button>
              )}
            </Flex>
          </Form>
        </Spin>
      </Modal>
      <DiscardConfirm
        open={showDiscardConfirm}
        onCancel={handleCancelDiscard}
        onConfirm={handleConfirmDiscard}
        title="Discard Changes?"
        description={t("global.discard_changes")}
        t={t}
      />
    </>
  )
}

DocumentsAddedModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isView: PropTypes.bool,
  isLoading: PropTypes.bool,
  setIsLoading: PropTypes.func.isRequired,
  initialData: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    document_url: PropTypes.string,
    document_filename: PropTypes.string,
    document_id: PropTypes.number,
    assigned_trips: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number
      })
    ),
    trip_ids: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
  })
}

DocumentsAddedModal.defaultProps = {
  initialData: null
}
